import styled, { css, keyframes } from 'styled-components';

import { colorUsage } from 'stylesheetB2c';

export const WORD_CLASS_NAME = 'word';
export const WORD_OUTLINE_CLASS_NAME = 'wordOutline';

const CURSOR_AREA_TOP_PLACEMENT_IN_PERCENT = 9;
const CURSOR_RELATIVE_WIDTH_IN_PERCENT = 50;
export const CURSOR_SWIPE_DURATION_IN_S = 1;
const CURSOR_FADE_DURATION_IN_S = 0.2;
const CURSOR_ANIMATION_DURATION_IN_S = CURSOR_SWIPE_DURATION_IN_S + CURSOR_FADE_DURATION_IN_S;
const WORD_TEXT_Z_INDEX = 1;
const WORD_CONTENT_DEFAULT_Z_INDEX = 0;

const getCursorAnimationPercentage = (timeInAnimationInS: number) =>
  Math.round((timeInAnimationInS / CURSOR_ANIMATION_DURATION_IN_S) * 100);

/* Animation inspired by https://codepen.io/P1N2O/pen/pyBNzX */
const wordAnimation = keyframes`
 0% { background-position: right center}
 100% { background-position: left center }
`;

export const SentenceContent = styled.div`
  position: relative;
  width: fit-content;
  height: 100%;
  text-align: center;
  overflow: visible;
  display: flex;
  justify-content: center;
  align-items: center;

  /* We style words here to optimize player performances */
  /* stylelint-disable-next-line selector-max-class */
  .${WORD_CLASS_NAME} {
    position: relative;
    z-index: ${WORD_TEXT_Z_INDEX};
    color: ${colorUsage.mp3KbpPlayer.lyricsFill};
    background-clip: text;
    -webkit-background-clip: text; /* stylelint-disable-line property-no-vendor-prefix */
    background-size: 200%;
    animation-name: ${wordAnimation};
    animation-timing-function: linear;
    animation-fill-mode: both;
    white-space: nowrap;
  }
`;
SentenceContent.displayName = 'SentenceContent';

interface SentenceOutlineProps {
  wordOutlineFontSize: number;
}
export const SentenceOutline = styled.div<SentenceOutlineProps>`
  position: absolute;
  top: 50%;
  transform: translate(0, -50%);
  left: 0;

  /* We style words outline here to optimize player performances */
  /* stylelint-disable-next-line selector-max-class */
  .${WORD_OUTLINE_CLASS_NAME} {
    ${({ wordOutlineFontSize }) => css`
      -webkit-text-stroke: ${wordOutlineFontSize}px ${colorUsage.mp3KbpPlayer.lyricsOutline};
    `}
    z-index: ${WORD_CONTENT_DEFAULT_Z_INDEX - 1};
    white-space: nowrap;
  }
`;
SentenceOutline.displayName = 'SentenceOutline';

interface CursorAreaProps {
  widthInPx: number;
  rightSpacingInPx: number;
}

export const CursorArea = styled.div<CursorAreaProps>`
  ${({ widthInPx, rightSpacingInPx }) => css`
    position: absolute;
    top: ${CURSOR_AREA_TOP_PLACEMENT_IN_PERCENT}%;
    left: ${-widthInPx - rightSpacingInPx}px;
    width: ${widthInPx}px;
    height: 60%;
    overflow: hidden;
  `}
`;
CursorArea.displayName = 'CursorArea';

const cursorAnimation = keyframes`
  0% {
    left: ${-CURSOR_RELATIVE_WIDTH_IN_PERCENT}%;
    opacity: 0;
  }

  ${getCursorAnimationPercentage(CURSOR_FADE_DURATION_IN_S)}% {
    opacity: 1;
  }

  ${getCursorAnimationPercentage(CURSOR_SWIPE_DURATION_IN_S)}% {
    left: ${100 - CURSOR_RELATIVE_WIDTH_IN_PERCENT}%;
    opacity: 1;
  }
  100% {
    left: ${100 - CURSOR_RELATIVE_WIDTH_IN_PERCENT}%;
    opacity: 0;
  }
`;

interface CursorProps {
  swipeColor: string;
  delayInMs: number;
  isPlaying: boolean;
}

export const Cursor = styled.div<CursorProps>`
  ${({ swipeColor, delayInMs, isPlaying }) => css`
    position: absolute;
    width: ${CURSOR_RELATIVE_WIDTH_IN_PERCENT}%;
    height: 100%;
    background-image: linear-gradient(
      to right,
      ${colorUsage.mp3KbpPlayer.cursorLeftBackground},
      ${swipeColor}
    );
    animation: ${cursorAnimation} ${CURSOR_ANIMATION_DURATION_IN_S}s linear ${delayInMs}ms both;
    animation-play-state: ${isPlaying ? 'running' : 'paused'};
  `}
`;
CursorArea.displayName = 'CursorArea';
